/** @jsxImportSource @emotion/react */
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import * as authSelectors from "../../auth/authSelectors";
import { Drawer } from "../../../components/Drawer";
import { ButtonAction } from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { StyledForm } from "../../../components/StyledForm";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { css } from "@emotion/react";
import { MutateProductTagPayload, ProductTag } from "../model";
import { useUpdateProductTag } from "../service/product-tags-api";
import { useCreateProductTag } from "../service/product-tags-api";
import { useEffect } from "react";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tag?: ProductTag;
};

const additionalFormStyles = css`
  gap: 16px;
`;

const validationSchema = yup.object({
  name: yup.string().required("Enter name of the product tag to continue"),
  title: yup.string().optional(),
});

export const MutateProductTagDrawer = ({ isOpen, onClose, tag }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const methods = useForm<MutateProductTagPayload>({
    defaultValues: {
      name: tag?.name || "",
      title: tag?.title || "",
    },
    resolver: yupResolver(validationSchema),
  });
  const { reset } = methods;

  const {
    mutateAsync: createProductTag,
    isLoading: isLoadingCreation,
  } = useCreateProductTag();

  const {
    mutateAsync: updateProductTag,
    isLoading: isLoadingUpdate,
  } = useUpdateProductTag();

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: MutateProductTagPayload) => {
    if (!data.title) {
      data.title = data.name;
    }
    try {
      if (tag) {
        await updateProductTag({
          organizationKeyName,
          data: {
            id: tag.id,
            ...data,
          },
        });
      } else {
        await createProductTag({
          organizationKeyName,
          data,
        });
      }
      toastr.success(
        "Success",
        `Product tag successfully ${tag ? "updated" : "created"}`
      );
      handleOnClose();
    } catch (error: any) {
      toastr.error("Error", error.message);
    }
  };

  useEffect(() => {
    if (!tag) {
      return;
    }
    reset({
      name: tag.name,
      title: tag.title,
    });
  }, [tag, reset]);

  return (
    <Drawer
      title={`${tag ? "Edit" : "Create"} product tag`}
      width={500}
      buttons={
        <ButtonAction onClick={methods.handleSubmit(onSubmit)} type="button">
          {isLoadingCreation || isLoadingUpdate ? <ActionSpinner /> : "Save"}
        </ButtonAction>
      }
      open={isOpen}
      onClose={handleOnClose}
    >
      <FormProvider {...methods}>
        <StyledForm additionalStyles={additionalFormStyles}>
          <TextInput
            label="Product tag name"
            name="name"
            helperText="Only visible to admins."
          />
          {/* Hidden for now, as it is not relevant as users cannot see it. */}
          {/* <TextInput
            label="Product tag title"
            name="title"
            helperText="Visible for users."
          /> */}
        </StyledForm>
      </FormProvider>
    </Drawer>
  );
};
